import React from "react";

import { Box, Card, Typography, useTheme } from "@mui/material";

interface Props {
  title: string;
  value: string | number;
  description: string;
  Icon: React.ElementType;
  color: string;
  disabled?: boolean;
}

export default function CardView({
  title,
  value,
  description,
  Icon,
  color,
  disabled = false,
}: Props) {
  const theme = useTheme();

  return (
    <Card
      sx={{
        flex: 1,
        minWidth: 0,
        borderRadius: "12px",
        border: `1px solid ${theme.palette.divider}`,
        borderLeft: `4px solid ${color}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        p: { xs: 2, md: 2.5 },
        opacity: disabled ? 0.5 : 1,
        backgroundColor: "#fff",
        transition: "box-shadow 0.2s ease",
        "&:hover": {
          boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
        },
      }}
    >
      {/* Top row: label + icon */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 600,
            color: theme.palette.text.secondary,
            textTransform: "uppercase",
            letterSpacing: "0.07em",
            lineHeight: 1.4,
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "8px",
            backgroundColor: `${color}18`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color,
            flexShrink: 0,
            ml: 1,
          }}
        >
          <Icon sx={{ fontSize: 20 }} />
        </Box>
      </Box>

      {/* Large value */}
      <Typography
        sx={{
          fontSize: { xs: 28, md: 32 },
          fontWeight: 700,
          color: theme.palette.text.primary,
          lineHeight: 1,
          mb: 0.5,
          letterSpacing: "-0.02em",
        }}
      >
        {value}
      </Typography>

      {/* Description */}
      <Typography
        sx={{
          fontSize: "12px",
          color: theme.palette.text.secondary,
          lineHeight: 1.4,
        }}
      >
        {description}
      </Typography>
    </Card>
  );
}
